import { CandidateData } from '@jobilla/nexus-sdk'
import { motion } from 'motion/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

type CandidateScoreStripPlotProps = {
	candidates: CandidateData[]
	isLoading?: boolean
}

type TooltipData = {
	name: string
	score: number
	x: number
	y: number
} | null

function getCandidateName(candidate: CandidateData, fallback: string): string {
	const meta = candidate.formSubmission?.meta
	if (meta && typeof meta === 'object' && 'name' in meta && typeof meta.name === 'string') {
		return meta.name
	}

	return fallback
}

export function CandidateScoreStripPlot({ candidates, isLoading }: CandidateScoreStripPlotProps) {
	const [tooltip, setTooltip] = useState<TooltipData>(null)
	const { t } = useTranslation('analytics')

	if (isLoading) {
		return (
			<div className="relative overflow-hidden p-6 rounded-xl border border-slate-200/60 bg-gradient-to-br from-slate-50 via-white to-slate-50/30 shadow-sm">
				<div className="absolute top-0 left-0 w-40 h-40 bg-gradient-to-br from-blue-100/30 to-transparent rounded-full blur-3xl -ml-20 -mt-20" />
				<div className="absolute bottom-0 right-0 w-40 h-40 bg-gradient-to-br from-purple-100/30 to-transparent rounded-full blur-3xl -mr-20 -mb-20" />
				<div className="relative">
					<h3 className="text-xl font-semibold bg-gradient-to-r from-grey-800 to-grey-700 bg-clip-text text-transparent mb-6">
						{t('score_distribution.title')}
					</h3>
					<div className="h-48 flex items-center justify-center">
						<div className="text-sm text-grey-500">{t('score_distribution.loading')}</div>
					</div>
				</div>
			</div>
		)
	}

	const candidatesWithScores = candidates.filter(
		(candidate) => candidate.formSubmission?.score?.percentage !== undefined,
	)

	if (candidatesWithScores.length === 0) {
		return (
			<div className="relative overflow-hidden p-6 rounded-xl border border-slate-200/60 bg-gradient-to-br from-slate-50 via-white to-slate-50/30 shadow-sm">
				<div className="absolute top-0 left-0 w-40 h-40 bg-gradient-to-br from-blue-100/30 to-transparent rounded-full blur-3xl -ml-20 -mt-20" />
				<div className="absolute bottom-0 right-0 w-40 h-40 bg-gradient-to-br from-purple-100/30 to-transparent rounded-full blur-3xl -mr-20 -mb-20" />
				<div className="relative">
					<h3 className="text-xl font-semibold bg-gradient-to-r from-grey-800 to-grey-700 bg-clip-text text-transparent mb-6">
						{t('score_distribution.title')}
					</h3>
					<div className="h-48 flex items-center justify-center">
						<div className="text-sm text-grey-500">{t('score_distribution.no_scores')}</div>
					</div>
				</div>
			</div>
		)
	}

	// The `!` assertions below are safe: candidatesWithScores filters on
	// form_submission?.score?.percentage !== undefined.
	const scores = candidatesWithScores.map((c) => c.formSubmission!.score!.percentage)
	const mean = scores.reduce((sum, score) => sum + score, 0) / scores.length
	const sortedScores = [...scores].sort((a, b) => a - b)
	const median = sortedScores[Math.floor(sortedScores.length / 2)]
	const stdDev = Math.sqrt(
		scores.reduce((sum, score) => sum + Math.pow(score - mean, 2), 0) / scores.length,
	)

	const bins: { [key: number]: CandidateData[] } = {}
	candidatesWithScores.forEach((candidate) => {
		const score = candidate.formSubmission!.score!.percentage
		const bin = Math.floor(score / 10) * 10
		if (!bins[bin]) {
			bins[bin] = []
		}
		bins[bin].push(candidate)
	})

	return (
		<>
			<div className="relative overflow-hidden p-8 rounded-xl border border-slate-200/60 bg-gradient-to-br from-slate-50 via-white to-slate-50/30 shadow-sm">
				<div className="relative">
					<div className="mb-4">
						<h2 className="text-xs uppercase tracking-wide font-semibold text-grey-500 mb-2">
							{t('score_distribution.title')}
						</h2>
						<p className="text-sm text-grey-600">
							{t('score_distribution.subtitle')}
						</p>
					</div>

					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.2, delay: 0.8 }}
						className="grid grid-cols-4 gap-6 mb-8"
					>
						<StatCard label={t('score_distribution.stat.total')} value={candidatesWithScores.length} />
						<StatCard label={t('score_distribution.stat.mean')} value={`${mean.toFixed(1)}%`} />
						<StatCard label={t('score_distribution.stat.median')} value={`${median.toFixed(1)}%`} />
						<StatCard label={t('score_distribution.stat.std_dev')} value={`${stdDev.toFixed(1)}%`} />
					</motion.div>

					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.2, delay: 0.9 }}
						className="relative"
					>
						<div className="flex">
							<div className="w-14 flex flex-col justify-between text-sm text-grey-600 py-6">
								<div>100%</div>
								<div>75%</div>
								<div>50%</div>
								<div>25%</div>
								<div>0%</div>
							</div>

							<div className="flex-1 relative h-96 border-l border-b border-slate-300 rounded-bl-lg bg-white/50">
								{[0, 25, 50, 75, 100].map((value) => (
									<div
										key={value}
										className="absolute left-0 right-0 border-t border-slate-200"
										style={{ bottom: `${value}%` }}
									/>
								))}

								<div
									className="absolute left-0 right-0 border-t-2 border-dashed border-emerald-400"
									style={{ bottom: `${mean}%` }}
								>
									<div className="absolute right-2 -top-2 text-xs text-emerald-600 bg-white px-1 rounded">
										{t('score_distribution.mean_label', { value: mean.toFixed(1) })}
									</div>
								</div>

								<div
									className="absolute left-0 right-0 border-t-2 border-dashed border-sky-400"
									style={{ bottom: `${median}%` }}
								>
									<div className="absolute left-2 -top-2 text-xs text-sky-600 bg-white px-1 rounded">
										{t('score_distribution.median_label', { value: median.toFixed(1) })}
									</div>
								</div>

								<div className="absolute inset-0 px-4">
									{candidatesWithScores.map((candidate, index) => {
										const score = candidate.formSubmission!.score!.percentage
										const bin = Math.floor(score / 10) * 10
										const indexInBin = bins[bin].findIndex((c) => c.id === candidate.id)
										const totalInBin = bins[bin].length

										const sectionWidth = 100 / 11
										const binCenter = (bin / 100) * 100 + sectionWidth / 2
										const spreadWidth = sectionWidth * 0.8
										const horizontalOffset = totalInBin > 1
											? ((indexInBin / (totalInBin - 1)) - 0.5) * spreadWidth
											: 0

										const color = getColorByScore(score)
										const candidateName = getCandidateName(
											candidate,
											t('score_distribution.unnamed_candidate'),
										)

										return (
											<motion.div
												key={candidate.id}
												initial={{ opacity: 0, scale: 0 }}
												animate={{ opacity: 1, scale: 1 }}
												transition={{
													duration: 0.2,
													delay: 0.9 + index * 0.005,
													type: 'spring',
													stiffness: 300,
												}}
												className={`absolute w-2.5 h-2.5 rounded-full ${color} cursor-pointer hover:scale-150 hover:z-50 transition-transform shadow-sm`}
												style={{
													bottom: `${score}%`,
													left: `${binCenter + horizontalOffset}%`,
													transform: 'translate(-50%, 50%)',
												}}
												onMouseEnter={(e) => {
													const rect = e.currentTarget.getBoundingClientRect()
													setTooltip({
														name: candidateName,
														score,
														x: rect.left + rect.width / 2,
														y: rect.top,
													})
												}}
												onMouseLeave={() => setTooltip(null)}
											/>
										)
									})}
								</div>
							</div>
						</div>

						<div className="mt-2 text-center text-xs text-grey-600">
							{t('score_distribution.score_range')}
						</div>
					</motion.div>

					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.2, delay: 1.0 }}
						className="mt-6 flex items-center justify-center gap-8 text-sm"
					>
						<div className="flex items-center gap-2">
							<div className="w-3 h-3 rounded-full bg-emerald-500" />
							<span className="text-grey-600">{t('score_distribution.legend.high')}</span>
						</div>
						<div className="flex items-center gap-2">
							<div className="w-3 h-3 rounded-full bg-amber-500" />
							<span className="text-grey-600">{t('score_distribution.legend.medium')}</span>
						</div>
						<div className="flex items-center gap-2">
							<div className="w-3 h-3 rounded-full bg-rose-500" />
							<span className="text-grey-600">{t('score_distribution.legend.low')}</span>
						</div>
					</motion.div>
				</div>
			</div>

			{tooltip && (
				<div
					className="fixed z-[9999] pointer-events-none"
					style={{
						left: `${tooltip.x}px`,
						top: `${tooltip.y - 10}px`,
						transform: 'translate(-50%, -100%)',
					}}
				>
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						className="bg-grey-900 text-white px-3 py-2 rounded-lg shadow-lg text-sm whitespace-nowrap"
					>
						<div className="font-semibold">{tooltip.name}</div>
						<div className="text-xs text-grey-300">
							{t('score_distribution.score_label', { score: tooltip.score.toFixed(1) })}
						</div>
					</motion.div>
				</div>
			)}
		</>
	)
}

function StatCard({ label, value }: { label: string; value: string | number }) {
	return (
		<div className="p-5 rounded-lg bg-white/70 border-2 border-slate-200/70 shadow-sm">
			<div className="text-xs font-semibold text-grey-500 uppercase tracking-wide mb-2">{label}</div>
			<div className="text-2xl font-bold text-grey-900">{value}</div>
		</div>
	)
}

function getColorByScore(score: number): string {
	if (score >= 70) { return 'bg-emerald-500' }
	if (score >= 40) { return 'bg-amber-500' }
	return 'bg-rose-500'
}
